package com.videoanalyzer.web.components

import androidx.compose.runtime.*
import kotlinx.browser.localStorage
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

/**
 * Cookie consent status stored in localStorage.
 */
object CookieConsentManager {
    private const val CONSENT_KEY = "videoscore_cookie_consent"
    private const val CONSENT_VERSION = "1" // Increment when cookie policy changes

    enum class ConsentStatus {
        NOT_SET,
        ACCEPTED_ALL,
        ESSENTIAL_ONLY
    }

    fun getConsentStatus(): ConsentStatus {
        val stored = localStorage.getItem(CONSENT_KEY)
        return when {
            stored == null -> ConsentStatus.NOT_SET
            stored.startsWith("accepted_all:$CONSENT_VERSION") -> ConsentStatus.ACCEPTED_ALL
            stored.startsWith("essential_only:$CONSENT_VERSION") -> ConsentStatus.ESSENTIAL_ONLY
            else -> ConsentStatus.NOT_SET // Old version, ask again
        }
    }

    fun setConsentStatus(status: ConsentStatus) {
        val value = when (status) {
            ConsentStatus.ACCEPTED_ALL -> "accepted_all:$CONSENT_VERSION"
            ConsentStatus.ESSENTIAL_ONLY -> "essential_only:$CONSENT_VERSION"
            ConsentStatus.NOT_SET -> return
        }
        localStorage.setItem(CONSENT_KEY, value)
    }

    fun hasAnalyticsConsent(): Boolean {
        return getConsentStatus() == ConsentStatus.ACCEPTED_ALL
    }
}

/**
 * Cookie consent banner that appears at the bottom of the screen.
 * Shows only if user hasn't given consent yet.
 */
@Composable
fun CookieConsentBanner() {
    var showBanner by remember {
        mutableStateOf(CookieConsentManager.getConsentStatus() == CookieConsentManager.ConsentStatus.NOT_SET)
    }

    if (!showBanner) return

    // Fixed banner at bottom of screen
    Div({
        style {
            position(Position.Fixed)
            bottom(0.px)
            left(0.px)
            right(0.px)
            backgroundColor(Color("#1e293b"))
            property("border-top", "1px solid rgba(255, 255, 255, 0.1)")
            padding(20.px)
            property("z-index", "9999")
            property("box-shadow", "0 -4px 20px rgba(0, 0, 0, 0.3)")
        }
    }) {
        Div({
            style {
                maxWidth(1200.px)
                margin(0.px, "auto".unsafeCast<CSSNumeric>())
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
                gap(16.px)
            }
        }) {
            // Text content
            Div({
                style {
                    display(DisplayStyle.Flex)
                    flexDirection(FlexDirection.Column)
                    gap(8.px)
                }
            }) {
                Div({
                    style {
                        display(DisplayStyle.Flex)
                        alignItems(AlignItems.Center)
                        gap(8.px)
                    }
                }) {
                    Span({
                        style { fontSize(20.px) }
                    }) { Text("\uD83C\uDF6A") } // Cookie emoji

                    Span({
                        style {
                            fontSize(16.px)
                            fontWeight(600)
                            color(Color("#f8fafc"))
                        }
                    }) { Text("Cookie Settings") }
                }

                P({
                    style {
                        fontSize(14.px)
                        color(Color("#94a3b8"))
                        lineHeight("1.5")
                        margin(0.px)
                    }
                }) {
                    Text("We use essential cookies to make VideoScore work (authentication, preferences). We'd also like to use analytics cookies to understand how you use our service and improve it. You can learn more in our Privacy Policy.")
                }
            }

            // Buttons
            Div({
                style {
                    display(DisplayStyle.Flex)
                    flexWrap(FlexWrap.Wrap)
                    gap(12.px)
                }
            }) {
                // Essential Only button
                Button({
                    style {
                        padding(12.px, 24.px)
                        backgroundColor(Color("transparent"))
                        border(1.px, LineStyle.Solid, Color("rgba(255, 255, 255, 0.2)"))
                        borderRadius(8.px)
                        color(Color("#94a3b8"))
                        fontSize(14.px)
                        fontWeight(500)
                        cursor("pointer")
                        property("transition", "all 0.2s ease")
                    }
                    onClick {
                        CookieConsentManager.setConsentStatus(CookieConsentManager.ConsentStatus.ESSENTIAL_ONLY)
                        showBanner = false
                    }
                }) {
                    Text("Essential Only")
                }

                // Accept All button
                Button({
                    style {
                        padding(12.px, 24.px)
                        property("background", "linear-gradient(135deg, #E53935 0%, #C62828 100%)")
                        border(0.px, LineStyle.None, Color.transparent)
                        borderRadius(8.px)
                        color(Color("#ffffff"))
                        fontSize(14.px)
                        fontWeight(600)
                        cursor("pointer")
                        property("transition", "all 0.2s ease")
                        property("box-shadow", "0 4px 15px rgba(229, 57, 53, 0.3)")
                    }
                    onClick {
                        CookieConsentManager.setConsentStatus(CookieConsentManager.ConsentStatus.ACCEPTED_ALL)
                        showBanner = false
                    }
                }) {
                    Text("Accept All Cookies")
                }
            }
        }
    }
}
